import React, { useState, useEffect } from 'react';
import { addCodingQuestionUploadApi, getFolderBySubTopicId } from '../../api/endpoints';
import ExcelJS from 'exceljs';
import { useNavigate, useLocation } from 'react-router-dom';

const norm = v => (v === null || v === undefined) ? '' : String(v).trim();

function mapCodingType(val) {
  if (!val) return false;
  if (val === 'testcases coding') return true;
  if (val === 'without testcases coding') return false;
  if (val === 'testcases') return true;
  if (val === 'without_testcases') return false;
  return true;
}

const AddCodingQuestion = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get navigation state (if any)
  const navState = location.state || {};
  const [formData, setFormData] = useState({
    topic: navState.topic || '',
    test_type: navState.test_type || 'Coding Test',
    subtopic: navState.subtopic || '',
    folder_name: navState.folder_name || '',
    file: null,
    isTestcase: mapCodingType(navState.codingType),
  });
  const [loading, setLoading] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);

  // Fetch folders matching topic and subtopic
  useEffect(() => {
    const fetchFolders = async () => {
      if (!formData.topic || !formData.subtopic) {
        setFolderOptions([]);
        return;
      }
      try {
        // Assuming getFolderBySubTopicId accepts topic and subtopic as params or you adjust accordingly
        // If it requires an ID, you might need to fetch that first or adjust the API call
        const response = await getFolderBySubTopicId({
          topic: formData.topic,
          sub_topic: formData.subtopic,
        });
        // Filter folders matching topic and sub_topic exactly (case-insensitive)
        const filteredFolders = (response || []).filter(
          folder =>
            folder.topic.toLowerCase() === formData.topic.toLowerCase() &&
            folder.sub_topic.toLowerCase() === formData.subtopic.toLowerCase()
        );
        setFolderOptions(filteredFolders);
      } catch (error) {
        console.error('Failed to fetch folder options:', error);
        setFolderOptions([]);
      }
    };

    fetchFolders();
  }, [formData.topic, formData.subtopic]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'topic') {
      setFormData(prev => ({
        ...prev,
        topic: value,
        subtopic: '',
        folder_name: ''
      }));
    } else if (name === 'subtopic') {
      setFormData(prev => ({
        ...prev,
        subtopic: value,
        folder_name: ''
      }));
    } else if (name === 'folder_name') {
      setFormData(prev => ({ ...prev, folder_name: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTestcaseChange = (e) => {
    setFormData(prev => ({ ...prev, isTestcase: e.target.checked }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData(prev => ({ ...prev, file }));
  };

  // Main submit logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      alert('Please choose a file to upload.');
      return;
    }
    setLoading(true);

    try {
      const data = await formData.file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(data);

      const worksheet = workbook.worksheets[0]; // First sheet
      const rows = [];

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // skip header
        rows.push(row.values.slice(1)); // slice(1) removes the empty first element
      });

      let created = 0;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        let folderNameExcel = '';
        let folderNameFinal = '';
        let payload = {};

        if (formData.isTestcase) {
          folderNameExcel = row[8] !== undefined && row[8] !== null ? String(row[8]).trim() : '';
          folderNameFinal = folderNameExcel || formData.folder_name || 'Overall';

          payload = {
            question_text: row[0] || '',
            test_case1: row[1] || '',
            test_case2: row[2] || '',
            test_case3: row[3] || '',
            answer: row[4] || '',
            mark: row[5] || 0,
            explain_answer: row[6] || '',
            input_format: row[7] || '',
            difficulty_level: row[9] || '',
            topic: formData.topic,
            test_type: formData.test_type,
            test_type_categories: 'PracticeTest',
            subtopic: formData.subtopic,
            folder_name: folderNameFinal,
            is_testcase: formData.isTestcase,
          };
        } else {
          folderNameExcel = row[5] !== undefined && row[5] !== null ? String(row[5]).trim() : '';
          folderNameFinal = folderNameExcel || formData.folder_name || 'Overall';

          payload = {
            question_text: row[0] || '',
            answer: row[1] || '',
            mark: row[2] || 0,
            explain_answer: row[3] || '',
            input_format: row[4] || '',
            difficulty_level: row[6] || '',
            topic: formData.topic,
            test_type: formData.test_type,
            test_type_categories: 'PracticeTest',
            subtopic: formData.subtopic,
            folder_name: folderNameFinal,
            is_testcase: formData.isTestcase,
          };
        }

        await addCodingQuestionUploadApi(payload);
        created++;
      }

      alert(`Excel data uploaded and saved successfully (${created} coding questions).`);
      navigate('/demo');
    } catch (error) {
      console.error(error);
      alert('Failed to upload Excel data.');
    } finally {
      setLoading(false);
    }
  };

  // Styling
  const labelStyle = { color: 'white' };
  const inputStyle = { width: '100%', background: '#323b44', color: 'white', border: '1px solid #555', borderRadius: '4px', padding: '5px' };

  return (
    <div style={{
      background: '#323b44',
      padding: '40px',
      borderRadius: '8px',
      maxWidth: '700px',
      margin: '40px auto',
      minHeight: '480px',
      color: 'white'
    }}>
      <h2 style={{ color: '#f1a128', marginBottom: '24px' }}>Add Coding Question</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Topic: </label>
            <input
              type="text"
              name="topic"
              value={formData.topic}
              onChange={handleInputChange}
              style={inputStyle}
              required
              readOnly={!!navState.topic}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Test Type: </label>
            <input
              type="text"
              name="test_type"
              value={formData.test_type}
              readOnly
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Sub Topic: </label>
            <input
              type="text"
              name="subtopic"
              value={formData.subtopic}
              onChange={handleInputChange}
              style={inputStyle}
              required
              readOnly={!!navState.subtopic}
            />
          </div>
         <div style={{ flex: 1 }}>
  <label style={labelStyle}>Folder (Optional): </label>
  <select
    name="folder_name"
    value={formData.folder_name}
    onChange={handleInputChange}
    style={inputStyle}
  >
    <option value="">Select Folder</option>
    {folderOptions.map(folder => (
      <option key={folder.id} value={folder.folder_name}>
        {folder.folder_name}
      </option>
    ))}
  </select>
</div>

        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>
            <input
              type="checkbox"
              checked={formData.isTestcase}
              onChange={handleTestcaseChange}
              style={{ marginRight: '8px' }}
            />
            Is Testcase
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ color: 'white', minWidth: '120px' }}>Upload File:</label>
          <input
            type="file"
            name="file"
            accept=".xls,.xlsx"
            onChange={handleFileChange}
            style={{ flex: 1, color: 'white' }}
          />
        </div>
        <div style={{ marginTop: '20px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              background: '#f1a128',
              color: 'black',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '4px',
              marginRight: '14px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Uploading...' : 'Submit'}
          </button>
          <button
            type="button"
            style={{
              background: '#f44336',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCodingQuestion;
